import { call, put, take, select } from 'redux-saga/effects'
import { noop } from 'redux-saga/utils'
import api from '../api/api'
import { normalize } from 'normalizr'
import coursesSchema from '../schemas/courses'
import boughtCoursesSchema from '../schemas/boughtCourses'
import contentsSchema from '../schemas/contents'
import { statusCodeIsBelow400, isResponseSuccessful } from '../helpers/utility'
import {
  FETCH_COURSE_TOPICS_REQUEST,
  FETCH_COURSE_TOPICS_SUCCESS,
  FETCH_COURSE_TOPICS_FAIL,
  FETCH_COURSE_TOPICS_ASYNC_START,
  FETCH_COURSE_TOPICS_ASYNC_END,
} from '../actions/actions'
import {
  FETCH_USER_COURSES_REQUEST,
  FETCH_USER_COURSES_SUCCESS,
  FETCH_USER_COURSES_FAIL,
  FETCH_USER_COURSES_ASYNC_END,
  FETCH_USER_COURSES_ASYNC_START,
} from '../actions/actions'
import {
  CREATE_COURSE_REQUEST,
  CREATE_COURSE_SUCCESS,
  CREATE_COURSE_FAIL,
  CREATE_COURSE_ASYNC_END,
  CREATE_COURSE_ASYNC_START,
} from '../actions/actions'
import {
  ATTACH_CONTENT_TO_COURSE_REQUEST,
  ATTACH_CONTENT_TO_COURSE_SUCCESS,
  ATTACH_CONTENT_TO_COURSE_FAIL,
  ATTACH_CONTENT_TO_COURSE_ASYNC_START,
  ATTACH_CONTENT_TO_COURSE_ASYNC_END,
} from '../actions/actions'
import {
  UPDATE_COURSE_INFO_REQUEST,
  UPDATE_COURSE_INFO_SUCCESS,
  UPDATE_COURSE_INFO_FAIL,
  UPDATE_COURSE_INFO_ASYNC_START,
  UPDATE_COURSE_INFO_ASYNC_END,
} from '../actions/actions'
import {
  FETCH_COURSE_CONTENTS_REQUEST,
  FETCH_COURSE_CONTENTS_FAIL,
  FETCH_COURSE_CONTENTS_SUCCESS,
  FETCH_COURSE_CONTENTS_ASYNC_START,
  FETCH_COURSE_CONTENTS_ASYNC_END,
} from '../actions/actions'
import {
  REMOVE_CONTENT_FROM_COURSE_REQUEST,
  REMOVE_CONTENT_FROM_COURSE_SUCCESS,
  REMOVE_CONTENT_FROM_COURSE_FAIL,
  REMOVE_CONTENT_FROM_COURSE_ASYNC_START,
  REMOVE_CONTENT_FROM_COURSE_ASYNC_END,
} from '../actions/actions'
import {
  UPDATE_COURSE_CONTENT_REQUEST,
  UPDATE_COURSE_CONTENT_SUCCESS,
  UPDATE_COURSE_CONTENT_FAIL,
  UPDATE_COURSE_CONTENT_ASYNC_START,
  UPDATE_COURSE_CONTENT_ASYNC_END,
} from '../actions/actions'
import {
  FETCH_COURSES_LIST_REQUEST,
  FETCH_COURSES_LIST_SUCCESS,
  FETCH_COURSES_LIST_FAIL,
  FETCH_COURSES_LIST_ASYNC_START,
  FETCH_COURSES_LIST_ASYNC_END,
} from '../actions/actions'
import {
  CREATE_COURSE_ORDER_REQUEST,
  CREATE_COURSE_ORDER_SUCCESS,
  CREATE_COURSE_ORDER_FAIL,
  CREATE_COURSE_ORDER_ASYNC_START,
  CREATE_COURSE_ORDER_ASYNC_END,
} from '../actions/actions'
import {
  FETCH_BOUGHT_COURSES_REQUEST,
  FETCH_BOUGHT_COURSES_SUCCESS,
  FETCH_BOUGHT_COURSES_FAIL,
  FETCH_BOUGHT_COURSES_ASYNC_END,
  FETCH_BOUGHT_COURSES_ASYNC_START,
} from '../actions/actions'
import {
  FETCH_SINGLE_COURSE_REQUEST,
  FETCH_SINGLE_COURSE_SUCCESS,
  FETCH_SINGLE_COURSE_FAIL,
  FETCH_SINGLE_COURSE_ASYNC_START,
  FETCH_SINGLE_COURSE_ASYNC_END,
} from '../actions/actions'

export function* fetchCourseTopics() {
  while (true) {
    const actionData = yield take(FETCH_COURSE_TOPICS_REQUEST)

    // read from cache if not forced to re-load
    const currentCourseTopics = yield select(state => state.courseTopics)
    if (!actionData.force && currentCourseTopics.length > 0) {
      yield put({
        type: FETCH_COURSE_TOPICS_SUCCESS,
        courseTopics: currentCourseTopics,
        meta: {
          fromCache: true,
        },
      })

      return
    }

    // otherwise load form the network
    yield put({ type: FETCH_COURSE_TOPICS_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors },
        },
      } = yield call(api.getCourseTopics)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: FETCH_COURSE_TOPICS_SUCCESS,
          courseTopics: data.course_topics,
        })
      } else {
        yield put({
          type: FETCH_COURSE_TOPICS_FAIL,
          error: general_errors,
        })
      }

      yield put({ type: FETCH_COURSE_TOPICS_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: FETCH_COURSE_TOPICS_FAIL, error })
      yield put({ type: FETCH_COURSE_TOPICS_ASYNC_END })
    }
  }
}

export function* fetchUserCourses() {
  while (true) {
    const { queryString } = yield take(FETCH_USER_COURSES_REQUEST)
    yield put({ type: FETCH_USER_COURSES_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors },
        },
      } = yield call(api.getUserCourses, queryString)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: FETCH_USER_COURSES_SUCCESS,
          courses: normalize(data.courses, coursesSchema),
          count: data.count,
        })
      } else {
        yield put({
          type: FETCH_USER_COURSES_FAIL,
          error: general_errors,
        })
      }

      yield put({ type: FETCH_USER_COURSES_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: FETCH_USER_COURSES_FAIL, error })
      yield put({ type: FETCH_USER_COURSES_ASYNC_END })
    }
  }
}

export function* fetchBoughtCourses() {
  while (true) {
    yield take(FETCH_BOUGHT_COURSES_REQUEST)
    yield put({ type: FETCH_BOUGHT_COURSES_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors },
        },
      } = yield call(api.getBoughtCourses)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: FETCH_BOUGHT_COURSES_SUCCESS,
          courses: normalize(data.course_orders, boughtCoursesSchema),
        })
      } else {
        yield put({
          type: FETCH_BOUGHT_COURSES_FAIL,
          error: general_errors,
        })
      }

      yield put({ type: FETCH_BOUGHT_COURSES_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: FETCH_BOUGHT_COURSES_FAIL, error })
      yield put({ type: FETCH_BOUGHT_COURSES_ASYNC_END })
    }
  }
}

export function* addCourse() {
  while (true) {
    const { formData, resolve, reject } = yield take(CREATE_COURSE_REQUEST)
    yield put({ type: CREATE_COURSE_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors, form_errors },
        },
      } = yield call(api.createCourse, formData)

      if (isResponseSuccessful(status, form_errors, general_errors)) {
        yield put({
          type: CREATE_COURSE_SUCCESS,
          course: data.course,
        })

        resolve(data.course)
      } else {
        yield put({
          type: CREATE_COURSE_FAIL,
          error: { general_errors, form_errors },
        })

        reject({ general_errors, form_errors })
      }

      yield put({ type: CREATE_COURSE_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: CREATE_COURSE_FAIL, error })
      yield put({ type: CREATE_COURSE_ASYNC_END })
    }
  }
}

export function* attachContentToCourse() {
  while (true) {
    const { formData, courseId, resolve, reject } = yield take(
      ATTACH_CONTENT_TO_COURSE_REQUEST
    )
    yield put({ type: ATTACH_CONTENT_TO_COURSE_ASYNC_START })

    try {
      const {
        data: {
          status,
          errors: { general_errors, form_errors },
        },
      } = yield call(api.attachContentToCourse, formData, courseId)

      if (isResponseSuccessful(status, form_errors, general_errors)) {
        yield put({
          type: ATTACH_CONTENT_TO_COURSE_SUCCESS,
        })

        resolve()
      } else {
        yield put({
          type: ATTACH_CONTENT_TO_COURSE_FAIL,
          error: { general_errors, form_errors },
        })

        reject({ general_errors, form_errors })
      }

      yield put({ type: ATTACH_CONTENT_TO_COURSE_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: ATTACH_CONTENT_TO_COURSE_FAIL, error })
      yield put({ type: ATTACH_CONTENT_TO_COURSE_ASYNC_END })
    }
  }
}

export function* updateCourseInfo() {
  while (true) {
    const { formData, courseId, resolve, reject } = yield take(UPDATE_COURSE_INFO_REQUEST)
    yield put({ type: UPDATE_COURSE_INFO_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors, form_errors },
        },
      } = yield call(api.updateCourseInfo, formData, courseId)

      if (isResponseSuccessful(status, form_errors, general_errors)) {
        yield put({
          type: UPDATE_COURSE_INFO_SUCCESS,
          course: data.course,
        })

        resolve()
      } else {
        yield put({
          type: UPDATE_COURSE_INFO_FAIL,
          error: { general_errors, form_errors },
        })

        reject({ general_errors, form_errors })
      }

      yield put({ type: UPDATE_COURSE_INFO_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: UPDATE_COURSE_INFO_FAIL, error })
      yield put({ type: UPDATE_COURSE_INFO_ASYNC_END })
    }
  }
}

export function* fetchCourseContents() {
  while (true) {
    const { courseId, resolve, reject } = yield take(FETCH_COURSE_CONTENTS_REQUEST)
    yield put({ type: FETCH_COURSE_CONTENTS_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors },
        },
      } = yield call(api.getContentOfCourse, courseId)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: FETCH_COURSE_CONTENTS_SUCCESS,
          contents: normalize(data.contents, contentsSchema),
        })

        resolve()
      } else {
        yield put({
          type: FETCH_COURSE_CONTENTS_FAIL,
          error: { general_errors },
        })

        reject({ general_errors })
      }

      yield put({ type: FETCH_COURSE_CONTENTS_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: FETCH_COURSE_CONTENTS_FAIL, error })
      yield put({ type: FETCH_COURSE_CONTENTS_ASYNC_END })
    }
  }
}

export function* updateContentOfCourse() {
  while (true) {
    const { courseId, attachmentId, formData, resolve, reject } = yield take(
      UPDATE_COURSE_CONTENT_REQUEST
    )
    yield put({ type: UPDATE_COURSE_CONTENT_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors, form_errors },
        },
      } = yield call(api.updateContentOfCourse, attachmentId, courseId, formData)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: UPDATE_COURSE_CONTENT_SUCCESS,
          content: data.content,
        })

        resolve()
      } else {
        yield put({
          type: UPDATE_COURSE_CONTENT_FAIL,
          error: { general_errors, form_errors },
        })

        reject({ general_errors, form_errors })
      }

      yield put({ type: UPDATE_COURSE_CONTENT_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: UPDATE_COURSE_CONTENT_FAIL, error })
      yield put({ type: UPDATE_COURSE_CONTENT_ASYNC_END })
    }
  }
}

export function* deleteContentOfCourse() {
  while (true) {
    const { courseId, attachmentId, resolve, reject } = yield take(
      REMOVE_CONTENT_FROM_COURSE_REQUEST
    )
    yield put({ type: REMOVE_CONTENT_FROM_COURSE_ASYNC_START })

    try {
      const {
        data: {
          status,
          errors: { general_errors },
        },
      } = yield call(api.deleteContentOfCourse, attachmentId, courseId)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: REMOVE_CONTENT_FROM_COURSE_SUCCESS,
          attachmentId,
          courseId,
        })

        resolve()
      } else {
        yield put({
          type: REMOVE_CONTENT_FROM_COURSE_FAIL,
          error: { general_errors },
        })

        reject({ general_errors })
      }

      yield put({ type: REMOVE_CONTENT_FROM_COURSE_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: REMOVE_CONTENT_FROM_COURSE_FAIL, error })
      yield put({ type: REMOVE_CONTENT_FROM_COURSE_ASYNC_END })
    }
  }
}

export function* fetchListOfAllCourses() {
  while (true) {
    const { queryString } = yield take(FETCH_COURSES_LIST_REQUEST)
    yield put({ type: FETCH_COURSES_LIST_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors },
        },
      } = yield call(api.getCoursesList, queryString)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: FETCH_COURSES_LIST_SUCCESS,
          courses: normalize(data.courses, coursesSchema),
          count: data.count,
        })
      } else {
        yield put({
          type: FETCH_COURSES_LIST_FAIL,
          error: { general_errors },
        })
      }

      yield put({ type: FETCH_COURSES_LIST_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: FETCH_COURSES_LIST_FAIL, error })
      yield put({ type: FETCH_COURSES_LIST_ASYNC_END })
    }
  }
}

export function* purchaseCourse() {
  while (true) {
    const { formData, resolve = noop, reject = noop } = yield take(
      CREATE_COURSE_ORDER_REQUEST
    )

    yield put({ type: CREATE_COURSE_ORDER_ASYNC_START })

    try {
      const {
        data: { data, status, errors },
      } = yield call(api.createCourseOrder, formData)

      if (statusCodeIsBelow400(status)) {
        yield put({
          type: CREATE_COURSE_ORDER_SUCCESS,
          order: data.course_order,
        })
        resolve(data.course_order)
      } else {
        yield put({ type: CREATE_COURSE_ORDER_FAIL, errors })
        reject(errors)
      }
      // yield put({ type: CREATE_COURSE_ORDER_ASYNC_END })
    } catch (error) {
      // reject('انجام درخواست مقدور نیست')
      yield put({ type: CREATE_COURSE_ORDER_FAIL, error })
    } finally {
      yield put({ type: CREATE_COURSE_ORDER_ASYNC_END })
      // console.log(error)
    }
  }
}

export function* fetchSingleCourse() {
  while (true) {
    const { courseId } = yield take(FETCH_SINGLE_COURSE_REQUEST)
    yield put({ type: FETCH_SINGLE_COURSE_ASYNC_START })

    try {
      const {
        data: {
          data,
          status,
          errors: { general_errors, form_errors },
        },
      } = yield call(api.getSingleCourseFull, courseId)

      if (isResponseSuccessful(status, form_errors, general_errors)) {
        yield put({
          type: FETCH_SINGLE_COURSE_SUCCESS,
          course: data.course,
        })
      } else {
        yield put({
          type: FETCH_SINGLE_COURSE_FAIL,
          form_errors,
          general_errors,
        })
      }

      yield put({ type: FETCH_SINGLE_COURSE_ASYNC_END })
    } catch (error) {
      console.log(error)
      yield put({ type: FETCH_SINGLE_COURSE_FAIL, error })
      yield put({ type: FETCH_SINGLE_COURSE_ASYNC_END })
    }
  }
}



// WEBPACK FOOTER //
// ./src/sagas/coursesSaga.js